// src/components/wellfinder/QueueProgress.jsx
//
// Orientation, not a dashboard. The analyst reads it when they arrive and when
// they finish a tier — never while working — so it is three compact blocks in
// the header region and nothing else.
//
// NO CHARTS. If a number needs a chart to be legible it needs fewer numbers.
//
// THE STATE THIS RENDERS IN FOR ITS ENTIRE LIFE UNTIL 3c SHIPS is: every
// counter zero, every rate undefined, calibration 0 of 200. That has to read as
// "not started" and not as "broken", which is why every zero is rendered as a
// deliberate value rather than suppressed, and why an undefined rate says so
// instead of showing 0.

const TIERS = ["c", "b", "a_prime", "a", "d"];
const TIER_LABEL = { a: "A", a_prime: "A′", b: "B", c: "C", d: "D" };

// Tier C carries roughly half the effort on 1.5% of the volume, and it is the
// only tier whose findings are unavailable from any state database. It gets its
// own line rather than a row in a table so it is legible on its own.
const TIER_ACCENT = {
  c: "text-accent-info",
  b: "text-fg-primary",
  a_prime: "text-fg-secondary",
  a: "text-fg-secondary",
  d: "text-fg-secondary",
};

const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);

function TierBar({ tier, done, total }) {
  if (!total) return null;
  const complete = done === total;
  return (
    <div className="flex items-center gap-2">
      <span className={`w-7 shrink-0 text-[11px] font-semibold ${TIER_ACCENT[tier]}`}>
        {TIER_LABEL[tier]}
      </span>
      <div className="h-1 flex-1 rounded bg-white/[0.06]">
        <div
          className={`h-1 rounded ${tier === "c" ? "bg-accent-info" : "bg-accent-info"}`}
          style={{ width: `${pct(done, total)}%` }}
        />
      </div>
      {/* "complete", not "164 / 164". A finished tier is a different fact from a
          tier that happens to have equal numbers, and an analyst scanning for
          what is left should not have to compare two figures to find out. */}
      <span className="w-24 shrink-0 text-right tabular-nums text-[11px] text-fg-secondary">
        {complete ? "complete" : `${done.toLocaleString()} / ${total.toLocaleString()}`}
      </span>
    </div>
  );
}

/**
 * One line. Everything the analyst needs on arrival and when a tier finishes,
 * which is when this is read — never during a three-second loop.
 */
function CollapsedStrip({ totals, tierRows, cal, bulkEnabled, areaName }) {
  return (
    <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 text-xs text-fg-secondary">
      <span className="tabular-nums text-fg-primary">
        {(totals.adjudicated ?? 0).toLocaleString()} / {(totals.total ?? 0).toLocaleString()}
      </span>
      {/* ── THE SCOPE, IN THE SENTENCE ──────────────────────────────────────
          This line carries two kinds of number: the counts and tier bars are
          AOI-scoped, A' calibration never is. A reviewer reading "1 / 4,647"
          beside "2,700 records" will assume both describe the area they picked.
          They do not — Oxy holds 4,647 records but only 922 A'.

          So the words carry the scope rather than a badge or an asterisk: the
          scoped numbers read "in <area>", the global one reads "all areas". A
          marker would need a legend; a phrase is already the sentence.

          Nothing renders when no area is selected — every number is global
          then, and saying so on each would be noise. */}
      {areaName && (
        <span className="truncate text-fg-secondary/70">in {areaName}</span>
      )}
      <span className="text-fg-secondary/60">
        {pct(totals.adjudicated ?? 0, totals.total ?? 0)}%
      </span>
      {tierRows.map((r) => (
        <span key={r.tier} className="tabular-nums">
          <span className={r.tier === "c" ? "text-accent-info" : "text-fg-secondary/70"}>
            {TIER_LABEL[r.tier]}
          </span>{" "}
          {r.done.toLocaleString()}/{r.total.toLocaleString()}
        </span>
      ))}
      {/* A' calibration rides on the collapsed line because it is the live
          deliverable — the number that decides whether 2,701 records can be
          bulk-confirmed. It comes off this line once that is settled. */}
      <span className="tabular-nums">
        A&prime; bulk:{" "}
        <span className={bulkEnabled ? "text-accent-info" : "text-fg-secondary/70"}>
          {bulkEnabled ? "enabled" : "disabled"}
        </span>{" "}
        {(cal.reviewed ?? 0).toLocaleString()}/{cal.sample_target ?? 200}
        {areaName && <span className="text-fg-secondary/60">{" "}all areas</span>}
      </span>
    </span>
  );
}

export default function QueueProgress({ stats, loading, error, expanded, areaName = null }) {
  if (error) {
    return (
      <div className="mb-3 text-xs text-red-400">
        Progress unavailable: {error.message}
      </div>
    );
  }
  if (loading && !stats) {
    return <div className="mb-3 text-xs text-fg-secondary">loading progress…</div>;
  }
  if (!stats) return null;

  const byTier = stats.by_tier_status ?? {};
  const totals = stats.totals ?? {};
  const cal = stats.aprime_calibration ?? {};
  const tp = stats.throughput ?? null;

  const decisionsTotal = stats.throughput?.decisions ?? 0;

  const tierRows = TIERS.map((t) => {
    const s = byTier[t] ?? {};
    const total = Object.values(s).reduce((a, b) => a + b, 0);
    const done = total - (s.pending ?? 0);
    return { tier: t, done, total };
  }).filter((r) => r.total > 0);


  if (!expanded) {
    return (
      <div data-testid="progress-collapsed" className="flex min-w-0 items-baseline gap-2">
        <CollapsedStrip
          totals={totals}
          tierRows={tierRows}
          cal={cal}
          bulkEnabled={stats.aprime_bulk_enabled}
          areaName={areaName}
        />
      </div>
    );
  }

  return (
    <div className="mt-2 mb-1">
      <div className="grid gap-x-8 gap-y-3 md:grid-cols-3 text-xs">
      {/* ── Where the work stands ─────────────────────────────────────────── */}
      <section className="min-w-0 md:col-span-1">
        <h2 className="mb-1.5 text-[11px] font-mono uppercase tracking-wide text-fg-secondary">
          Adjudicated{areaName ? ` in ${areaName}` : ""}
        </h2>
        <p className="mb-2 tabular-nums text-fg-primary">
          {(totals.adjudicated ?? 0).toLocaleString()}
          <span className="text-fg-secondary">
            {" "}/ {(totals.total ?? 0).toLocaleString()}
          </span>
          <span className="ml-2 text-fg-secondary">
            {pct(totals.adjudicated ?? 0, totals.total ?? 0)}%
          </span>
        </p>
        {/* Per tier, because a single "N of 10,803" is close to meaningless when
            the tiers differ this much in cost: C is 1.5% of volume and about
            half the effort. */}
        <div className="space-y-1">
          {tierRows.map((r) => <TierBar key={r.tier} {...r} />)}
        </div>
      </section>

      {/* ── THROUGHPUT: DELIBERATELY ABSENT ───────────────────────────────────
          Every verdict in the corpus was produced while building and debugging
          this interface — between layout fixes, permission failures and page
          reloads, with attention on the UI rather than the imagery. A rate
          computed from that measures development, not review.
          
          Relabelling it does not help and neither does a caveat: both the 14/hr
          and the 205/hr figure describe the same wrong activity, and a caveat
          cannot rescue a measurement of something else. The block returns when
          the focus heartbeat has data from real review, and the copy below says
          so rather than leaving a gap the reader fills in.
          
          PROJECTED-HOURS-TO-CLEAR is gone for the same reason: it was derived
          from those rates. */}
      <section className="min-w-0">
        <h2 className="mb-1.5 text-[11px] font-mono uppercase tracking-wide text-fg-secondary">
          Throughput
        </h2>
        <p className="text-fg-secondary">
          No decisions recorded yet — nothing to measure.
        </p>
        <p className="mt-1 text-[11px] leading-snug text-fg-secondary/70">
          Measurement begins when adjudication does. Verdicts produced while
          developing the interface are excluded, so rate, dwell and hours-to-clear
          appear once real review starts.
        </p>
      </section>

      {/* ── Whether A′ bulk is available ──────────────────────────────────── */}
      <section className="min-w-0">
        <h2 className="mb-1.5 text-[11px] font-mono uppercase tracking-wide text-fg-secondary">
          A′ bulk confirmation
        </h2>
        <p className="mb-1.5">
          <span
            className={
              stats.aprime_bulk_enabled ? "text-accent-info" : "text-fg-primary"
            }
          >
            {stats.aprime_bulk_enabled ? "Enabled" : "Disabled"}
          </span>
          <span className="ml-2 text-fg-secondary">
            {(cal.total ?? 0).toLocaleString()} records
            {/* Never AOI-scoped. The calibration protocol is a FIXED sample —
                the first 200 A' reviewed individually — and narrowing the
                denominator to an area does not narrow the protocol, it breaks
                the comparison the bulk-confirm decision rests on. */}
            {areaName ? " across all areas" : ""}
          </span>
        </p>
        {/* The flag on its own is a boolean nobody can explain. What turns the
            calibration protocol from a paragraph in a design doc into something
            visible is the progress toward it and the number it is measuring. */}
        <p className="mb-1 tabular-nums text-fg-secondary">
          Reviewed individually: {(cal.reviewed ?? 0).toLocaleString()} /{" "}
          {cal.sample_target ?? 200}
        </p>
        <div className="mb-1.5 h-1 rounded bg-white/[0.06]">
          <div
            className="h-1 rounded bg-accent-info"
            style={{
              width: `${Math.min(100, pct(cal.reviewed ?? 0, cal.sample_target || 200))}%`,
            }}
          />
        </div>
        {/* ABSENT until measured, not a line saying so. The null-vs-zero
            distinction still matters and is still honoured — it is just
            expressed by the row not existing rather than by a disclaimer
            occupying it. */}
        {cal.override_rate != null && (
          <p className="tabular-nums text-fg-secondary">
            Override rate: {(cal.override_rate * 100).toFixed(1)}%
            <span className="ml-1 text-fg-secondary/70">
              ({cal.overrode_rank1} of {cal.matches_set})
            </span>
          </p>
        )}
      </section>
      </div>
    </div>
  );
}
